import { Experiment } from './experimentTypes';

// experiment ids — these are used for convenience in code when testing for experiment matches
export enum ExperimentId {
    betterAuth = 'betterAuth',
    disableInviteLimit = 'disableInviteLimit',
    disableAutoShiftLimit = 'disableAutoShiftLimit',
    bypassRecaptchaChecks = 'bypassRecaptchaChecks',
    publicTemplateCreation = 'publicTemplateCreation',
    renderSecureImagesThroughCF = 'renderSecureImagesThroughCF',
    // Custom templates weren't originally migrated across to neo4j, so we migrate them lazily as well
    customTemplatesGraphMigration = 'customTemplatesGraphMigration',
    aliasBreadcrumbPreference = 'aliasBreadcrumbPreference',

    // A lever to pull if the graph DB is becoming overwhelmed & we want to direct some traffic elsewhere temporarily
    disableGraphDbAppInit = 'disableGraphDbAppInit',

    // Migration flags
    // NOTE: These are not real experiments, they are just used to track the progress of migrations.
    //   They are added to the user's flags object when their data has been migrated.
    //   This allows the server to check if the user supports graph DB syncing by looking at
    //   the user alone, without performing mongo queries in potentially hot areas of the code.
    graphDbSynced = 'graphDbSynced',
    boardSearchIndexComplete = 'boardSearchIndexComplete',

    clientPersistence = 'clientPersistence',

    // Fetch locales from CDN instead of using the local bundle
    //  NOTE: This is used by translators to quickly check their translations in app (so is long-lived)
    fetchLocalesFromCDN = 'fetchLocalesFromCDN',

    // App multi locale rollout flags
    appLocaleGerman = 'appLocaleGerman',
    appLocalePortugueseBrazilian = 'appLocalePortugueseBrazilian',
    appLocaleChineseSimplified = 'appLocaleChineseSimplified',
    appLocaleRussian = 'appLocaleRussian',
    appLocalePolish = 'appLocalePolish',
    appLocaleJapanese = 'appLocaleJapanese',
    appLocaleDutch = 'appLocaleDutch',
    appLocaleItalian = 'appLocaleItalian',
    appLocaleKorean = 'appLocaleKorean',

    tableTiptapV3 = 'tableTiptapV3',

    // Tags feature (we're still using this for 'Labels' to prevent any possible
    // issues that may be caused by renaming this)
    tags = 'tags',

    // Copy image data
    copyImageData = 'copyImageData',

    // Image modal V2 — expanded image view with bottom toolbar
    imageModalV2 = 'imageModalV2',
}

// These experiments will be created in the database if they don't exist
// so that experiments ids can be easily synced between environments.
export const DEFAULT_EXPERIMENTS: Partial<Experiment>[] = [
    {
        _id: ExperimentId.betterAuth,
        title: 'User flag: Better Auth migration',
    },
    {
        _id: ExperimentId.disableInviteLimit,
        title: 'User flag: disable invite limit',
    },
    {
        _id: ExperimentId.disableAutoShiftLimit,
        title: 'User flag: disable auto shift limit',
    },
    {
        _id: ExperimentId.bypassRecaptchaChecks,
        title: 'User flag: disable auto shift limit',
    },
    {
        _id: ExperimentId.publicTemplateCreation,
        title: 'User flag: allow public template creation',
        staffEnabled: true,
    },
    {
        _id: ExperimentId.renderSecureImagesThroughCF,
        title: 'Image security: render via CloudFront',
    },
    {
        _id: ExperimentId.disableGraphDbAppInit,
        title: 'Disable graph DB for app init',
    },
    {
        _id: ExperimentId.aliasBreadcrumbPreference,
        title: 'Prefer explicit alias path in client breadcrumbs',
    },
    {
        _id: ExperimentId.clientPersistence,
        title: 'Use IndexedDB to cache elements',
    },
    {
        _id: ExperimentId.tableTiptapV3,
        title: 'Enable version 3 of the table Tiptap architecture',
    },
    {
        _id: ExperimentId.fetchLocalesFromCDN,
        title: 'Fetch i18n locale files from CDN',
    },
    {
        _id: ExperimentId.appLocaleGerman,
        title: 'App locale rollout: German',
    },
    {
        _id: ExperimentId.appLocalePortugueseBrazilian,
        title: 'App locale rollout: Portuguese (Brazilian)',
    },
    {
        _id: ExperimentId.appLocaleChineseSimplified,
        title: 'App locale rollout: Chinese (Simplified)',
    },
    {
        _id: ExperimentId.appLocaleRussian,
        title: 'App locale rollout: Russian',
    },
    {
        _id: ExperimentId.appLocalePolish,
        title: 'App locale rollout: Polish',
    },
    {
        _id: ExperimentId.appLocaleJapanese,
        title: 'App locale rollout: Japanese',
    },
    {
        _id: ExperimentId.appLocaleDutch,
        title: 'App locale rollout: Dutch',
    },
    {
        _id: ExperimentId.appLocaleItalian,
        title: 'App locale rollout: Italian',
    },
    {
        _id: ExperimentId.appLocaleKorean,
        title: 'App locale rollout: Korean',
    },
    {
        _id: ExperimentId.tags,
        title: 'Enable adding/editing tags on elements',
    },
    {
        _id: ExperimentId.copyImageData,
        title: 'Enable copying image data to the clipboard',
    },
    {
        _id: ExperimentId.imageModalV2,
        title: 'Enable image modal V2 view',
    },
];

// These are the fields from the user model that will be taken into account when
// determining if a user is matched to an experiment.
export const USER_MODEL_EXPERIMENT_PROJECTION = {
    flags: 1,
    role: 1,
};
